import { useCallback, useEffect, useMemo, useState } from "react";
import type { Job, JobOtherExpense, JobOtherExpenseCategory } from "@/data/types";
import { JobService } from "@/services/job.service";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Receipt, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EXPENSE_CATEGORY_LABELS: Record<JobOtherExpenseCategory, string> = {
  fuel_logistics: "Gas / Fuel / Logistics",
  dumpster_disposal: "Dumps / Trash / Disposal",
  misc_operational: "Other Expense",
};

type EditableOtherExpense = JobOtherExpense & {
  editorId: string;
};

function createEditableOtherExpense(expense?: Partial<JobOtherExpense>): EditableOtherExpense {
  return {
    editorId: crypto.randomUUID(),
    id: String(expense?.id ?? crypto.randomUUID()),
    category: (expense?.category as JobOtherExpenseCategory | undefined) ?? "misc_operational",
    amount: typeof expense?.amount === "number" ? expense.amount : 0,
    incurredOn: String(expense?.incurredOn ?? "").trim() || undefined,
    note: String(expense?.note ?? "").trim() || undefined,
  };
}

function getBaseFinancials(job: Job): NonNullable<Job["financials"]> {
  return job.financials ?? {
    contractValue: 0,
    estimatedMaterialCost: 0,
    estimatedLaborCost: 0,
    subcontractorPayout: 0,
    marginEstimate: 0,
    paymentStatus: "Not Invoiced",
    payoutStatus: "Not Submitted",
    invoices: [],
    payments: [],
  };
}

function normalizeExpenseDraft(expense: EditableOtherExpense): JobOtherExpense {
  return {
    id: expense.id || crypto.randomUUID(),
    category: expense.category,
    amount: Math.max(0, Number(expense.amount) || 0),
    incurredOn: String(expense.incurredOn ?? "").trim() || undefined,
    note: String(expense.note ?? "").trim() || undefined,
  };
}

function formatMoney(value: number) {
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

interface Props {
  job: Job;
  canManage: boolean;
  onJobUpdate: (job: Job) => void;
}

export function JobOtherExpensesCard({ job, canManage, onJobUpdate }: Props) {
  const [draft, setDraft] = useState<EditableOtherExpense[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const seeded = (job.financials?.otherExpenses ?? []).map((expense) => createEditableOtherExpense(expense));
    setDraft(seeded);
  }, [job.financials?.otherExpenses]);

  const total = useMemo(
    () => draft.reduce((sum, row) => sum + Math.max(0, Number(row.amount) || 0), 0),
    [draft],
  );

  const dirty = useMemo(() => {
    const current = JSON.stringify((job.financials?.otherExpenses ?? []).map((expense) => normalizeExpenseDraft(createEditableOtherExpense(expense))));
    const next = JSON.stringify(draft.map((expense) => normalizeExpenseDraft(expense)));
    return current !== next;
  }, [draft, job.financials?.otherExpenses]);

  const handleAdd = useCallback(() => {
    setDraft((prev) => [...prev, createEditableOtherExpense()]);
  }, []);

  const handleUpdate = useCallback((editorId: string, updates: Partial<EditableOtherExpense>) => {
    setDraft((prev) => prev.map((row) => (row.editorId === editorId ? { ...row, ...updates } : row)));
  }, []);

  const handleRemove = useCallback((editorId: string) => {
    setDraft((prev) => prev.filter((row) => row.editorId !== editorId));
  }, []);

  const handleSave = useCallback(async () => {
    if (!canManage || saving) return;

    const normalized = draft
      .map((expense) => normalizeExpenseDraft(expense))
      .filter((expense) => expense.amount > 0 || expense.note || expense.incurredOn);

    const invalid = normalized.find((expense) => expense.amount <= 0);
    if (invalid) {
      toast.error("Expense amount is required", {
        description: "Enter an amount greater than zero for each saved expense.",
      });
      return;
    }

    setSaving(true);
    try {
      const nextFinancials = {
        ...getBaseFinancials(job),
        otherExpenses: normalized,
      };
      const { data, error } = await JobService.update(job.id, {
        financials: nextFinancials,
      });
      if (error || !data) throw error || new Error("Failed to save other expenses");
      onJobUpdate(data);
      toast.success("Other expenses saved", {
        description: normalized.length > 0 ? `${normalized.length} expense row${normalized.length === 1 ? "" : "s"} updated.` : "Cleared all additional expenses.",
      });
    } catch (error) {
      console.error("[JobOtherExpensesCard] Failed to save other expenses:", error);
      toast.error("Failed to save other expenses");
    } finally {
      setSaving(false);
    }
  }, [canManage, draft, job, onJobUpdate, saving]);

  return (
    <Card className="shadow-none">
      <CardContent className="p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Receipt className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Other Job Expenses</h3>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Track direct job expenses like gas, dumps/trash, and miscellaneous field costs so Job Economics stays accurate.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="rounded-md border bg-muted/20 px-3 py-2 text-right">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Total</p>
              <p className="text-sm font-semibold text-foreground">{formatMoney(total)}</p>
            </div>
            {canManage && (
              <Button type="button" variant="outline" size="sm" className="gap-1" onClick={handleAdd}>
                <Plus className="h-3.5 w-3.5" />
                Add Expense
              </Button>
            )}
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {draft.length === 0 ? (
            <div className="rounded-lg border border-dashed px-4 py-6 text-sm text-muted-foreground">
              No other expenses yet. Add gas, dumps/trash, or any job-specific operating cost here.
            </div>
          ) : (
            draft.map((expense, index) => (
              <div key={expense.editorId} className="rounded-lg border p-3">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                      Expense {index + 1}
                    </p>
                    <p className="text-sm font-medium text-foreground">
                      {EXPENSE_CATEGORY_LABELS[expense.category]}
                    </p>
                  </div>
                  {canManage && (
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleRemove(expense.editorId)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>

                <div className="grid gap-3 md:grid-cols-3">
                  <div className="space-y-1">
                    <Label>Category</Label>
                    <Select
                      value={expense.category}
                      onValueChange={(value) => handleUpdate(expense.editorId, { category: value as JobOtherExpenseCategory })}
                      disabled={!canManage}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(EXPENSE_CATEGORY_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label>Amount</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={expense.amount || ""}
                      onChange={(event) => handleUpdate(expense.editorId, { amount: Number(event.target.value) || 0 })}
                      disabled={!canManage}
                    />
                  </div>

                  <div className="space-y-1">
                    <Label>Incurred On</Label>
                    <Input
                      type="date"
                      value={expense.incurredOn ?? ""}
                      onChange={(event) => handleUpdate(expense.editorId, { incurredOn: event.target.value || undefined })}
                      disabled={!canManage}
                    />
                  </div>
                </div>

                <div className="mt-3 space-y-1">
                  <Label>Note</Label>
                  <Textarea
                    value={expense.note ?? ""}
                    onChange={(event) => handleUpdate(expense.editorId, { note: event.target.value || undefined })}
                    placeholder="Example: dump run after demo, gas reimbursement, emergency store run..."
                    disabled={!canManage}
                  />
                </div>
              </div>
            ))
          )}
        </div>

        {canManage && (
          <div className="mt-4 flex justify-end">
            <Button type="button" onClick={() => void handleSave()} disabled={!dirty || saving}>
              {saving ? "Saving..." : "Save Other Expenses"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
